<template>
  <div
    class="preview-body"
    @pointerdown="onCanvasDeselect"
    @contextmenu="$event.preventDefault()"
  >
    <div
      :class="['preview-body__canvas-area', 
      {
        'preview-body__canvas-area--drag-over': isDragged, 
        'preview-body__canvas-area--drag-leave': dragLeave
      }]">
      <slot name="canvas" />
      <transition name="preview-body__fade-transition">
        <div v-show="showEffectPauseMessage" class="preview-body__effect-pause-message">
          <pause-icon />
          effects paused
        </div>
      </transition>
      <audio-upload-prompt
        class="preview-body__audio-upload-prompt"
        v-show="showPrompt"
        :is-dragged="isDragged"
        :load-state="loadState"
        :file-name="fileName"
        @file-selected="onFileSelected"
      />
      <spinner
        class="preview-body__loading-spinner"
        v-show="isLoading"
      />
    </div>
  </div>
</template>
<script setup lang="ts">
import AudioUploadPrompt from '@/components/preview/AudioUploadPrompt.vue'
import PauseIcon from '@/assets/PauseIcon.svg'
import Spinner from '@/components/common/Spinner.vue'
import { useApplicationStore } from '@/store/application-store'
import { computed, ref, watch } from 'vue'
import useTextStates from '@/composables/UseTextStates'
import { AudioLoadState } from '@/audio/types'

const showEffectPauseMessage  = ref(false)
let effectPauseMessageTimeoutId: ReturnType<typeof setTimeout>
const applicationStore = useApplicationStore()

const { saveText } = useTextStates()

const props = defineProps<{
  showPrompt: boolean
  isDragged: boolean
  loadState: AudioLoadState
  fileName: string
}>()

const isLoading = computed(() => !props.showPrompt && (applicationStore.isBackgroundLoading || applicationStore.isStickerLoading))

const emits = defineEmits<{
  (event: 'fileSelected', file: File | string, fileName?: string): void,
}>()

// This ref is required to only show the exit animation when dragging has ended. This is only possible to do if we
// activate the animation after the _first_ time that dragging ends - otherwise we end up with the animation on mount
const dragLeave = ref(false)

watch(() => props.isDragged, () => {
  dragLeave.value = !props.isDragged
})

const onCanvasDeselect = (event: Event) => {
  // Make sure the area clicked is the preview body itself and not it's children
  if (event.target === event.currentTarget) {
    // deselect the canvas selected entities
    if ( !props.showPrompt && applicationStore.editTextState === 'none') {
      applicationStore.deselectAllEntities()
    }
    
    // Save text on navigating away from editing mode
    if ( applicationStore.editTextState !== 'none' && applicationStore.currentEditingText && applicationStore.currentEditingText.text.trim() !== '') {
      saveText(applicationStore.currentEditingText)
    }
  }
}

const onFileSelected = (file: File | string, fileName?: string) => {
  emits('fileSelected', file, fileName)
}

watch(() => applicationStore.disableEffectsOnPreview, () => {
  if (applicationStore.selectedText !== null && applicationStore.editTextState !== 'none') {
    return
  }
  clearTimeout(effectPauseMessageTimeoutId)
  showEffectPauseMessage.value = applicationStore.disableEffectsOnPreview
  if (showEffectPauseMessage.value) {
    effectPauseMessageTimeoutId = setTimeout(() => showEffectPauseMessage.value = false, 1300)
  }
})
</script>
<style lang="scss">
.preview-body {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: $grey-90;

  &__canvas-area {
    position: relative;

    &::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
      pointer-events: none;
    }

    &--drag-over::after {
      animation: outline-in 200ms $animationCurve forwards;
    }
    
    &--drag-leave::after {
      animation: outline-out 200ms $animationCurve forwards;
    }
  }

  &__audio-upload-prompt {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
  }

  &__loading-spinner {
    position: absolute;
    width: 32px;
    height: 32px;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    margin: auto;
  }

  &__effect-pause-message {
    position: absolute;
    font-family: $font-medium;
    color: $white;
    width: 100%;
    text-align: center;
    top: 9px;
    pointer-events: none;
    filter: drop-shadow(1px 1px 2px $black);
  }

  &__fade-transition {
    &-enter-active,
    &-leave-active {
      transition: opacity 300ms $animationCurve;
    }

    &-enter-from,
    &-leave-to {
      opacity: 0;
    }
  }

  @keyframes outline-in {
    0% {
      border-color: rgba($purple, 1.0);
      border-width: 0px;
    }
    100% {
      border-width: 1px;
      border-color: rgba($purple, 0.5);
      border-style: dashed;
    }
  }

  @keyframes outline-out {
    0% {
      border-width: 1px;
      border-color: rgba($purple, 0.5);
      border-style: dashed;
    }
    100% {
      border-color: rgba($purple, 1.0);
      border-width: 0px;
    }
  }
}
</style>
